import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { USER_MANAGEMENT_API_CONFIG, UserManagementConfig } from '../config';
import {
  OrganizationUser,
  OrganizationUserDetailed,
  OrganizationUserQueryParams,
  ChangeUserRoleDto,
  RemoveUserDto,
  GetOrganizationUsersResponse,
  GetOrganizationUserResponse,
  ChangeUserRoleResponse,
  RemoveUserResponse,
  RemoveMemberResponse
} from '../models';

/**
 * Service for managing users within organizations via the User Management API
 * 
 * This service provides comprehensive methods for:
 * - Listing users in an organization
 * - Viewing user details with organization role
 * - Changing user roles within organization
 * - Removing users from organization
 * 
 * @remarks
 * **Authentication:** All endpoints require authentication via Bearer token.
 * **Authorization:** All endpoints require Organization Admin role.
 * 
 * **Access Control:**
 * - Only organization Admins can access these endpoints
 * - Operations are scoped to a specific organization
 * - Cannot remove organization creator
 * 
 * @example
 * ```typescript
 * constructor(private userMgmtService: UserManagementService) {}
 * 
 * // Get users in organization
 * this.userMgmtService.getOrganizationUsers('orgId').subscribe(response => {
 *   console.log('Users:', response.data.users);
 *   console.log('Total:', response.data.pagination.total);
 * });
 * 
 * // Change user role
 * this.userMgmtService.changeUserRole('orgId', 'userId', { role: 'Admin' })
 *   .subscribe(response => {
 *     console.log('Role updated:', response.data.user);
 *   });
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class UserManagementService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(USER_MANAGEMENT_API_CONFIG);
  private readonly apiUrl: string;

  constructor() {
    this.apiUrl = `${this.config.apiBaseUrl}/api/user-management`;
    this.logDebug('UserManagementService initialized', { apiUrl: this.apiUrl });
  }

  // ============================================================================
  // READ OPERATIONS
  // ============================================================================

  /**
   * Get users in an organization
   * 
   * @param orgId - Organization ID
   * @param params - Query parameters for filtering and pagination
   * @returns Observable of API response with users and pagination
   * 
   * @throws {Error} 400 - Invalid organization ID
   * @throws {Error} 401 - Unauthorized
   * @throws {Error} 403 - Forbidden (Admin role required)
   * @throws {Error} 404 - Organization not found
   * 
   * @remarks
   * **Admin Only** - Requires Admin role in the organization.
   * 
   * **Query Parameters:**
   * - `page`: Page number (default: 1, min: 1)
   * - `limit`: Items per page (default: 10, max: 100)
   * - `search`: Search by name or email (max 100 chars)
   * - `role`: Filter by specific role (Admin, Editor, Reader)
   * 
   * **Response:**
   * - Users array with organization-specific role information
   * - Pagination metadata (total, page, limit, totalPages, hasMore)
   * - Organization context (id, name, orgCode)
   * 
   * @example
   * ```typescript
   * // Get first page
   * this.userMgmtService.getOrganizationUsers('507f1f77bcf86cd799439011')
   *   .subscribe({
   *     next: response => {
   *       console.log('Org:', response.data.organization.name);
   *       console.log('Users:', response.data.users);
   *       console.log('Total:', response.data.pagination.total);
   *     },
   *     error: err => console.error('Failed:', err)
   *   });
   * 
   * // Search users
   * this.userMgmtService.getOrganizationUsers('orgId', {
   *   search: 'john',
   *   page: 1,
   *   limit: 20
   * }).subscribe(response => {
   *   console.log('Search results:', response.data.users);
   * });
   * 
   * // Filter by role
   * this.userMgmtService.getOrganizationUsers('orgId', {
   *   role: 'Admin'
   * }).subscribe(response => {
   *   console.log('Admins:', response.data.users);
   * });
   * ```
   */
  getOrganizationUsers(
    orgId: string,
    params?: OrganizationUserQueryParams
  ): Observable<GetOrganizationUsersResponse> {
    this.logDebug('Getting organization users', { orgId, params });

    let httpParams = new HttpParams();
    if (params) {
      if (params.page !== undefined) httpParams = httpParams.set('page', params.page.toString());
      if (params.limit !== undefined) httpParams = httpParams.set('limit', params.limit.toString());
      if (params.search) httpParams = httpParams.set('search', params.search);
      if (params.role) httpParams = httpParams.set('role', params.role);
    }

    return this.http
      .get<GetOrganizationUsersResponse>(
        `${this.apiUrl}/organizations/${orgId}/users`,
        { params: httpParams }
      )
      .pipe(
        map(response => {
          this.logDebug('Organization users retrieved', { 
            count: response.data.users.length,
            total: response.data.pagination.total 
          });
          return response;
        }),
        catchError(error => this.handleError('getOrganizationUsers', error))
      );
  }

  /**
   * Get user details in organization context
   * 
   * @param orgId - Organization ID
   * @param userId - User ID
   * @returns Observable of API response with user details
   * 
   * @throws {Error} 400 - Invalid ID
   * @throws {Error} 401 - Unauthorized
   * @throws {Error} 403 - Forbidden (Admin role required)
   * @throws {Error} 404 - Organization or user not found
   * 
   * @remarks
   * **Admin Only** - Requires Admin role in the organization.
   * 
   * Returns detailed user information including:
   * - Organization-specific role
   * - Additional profile fields (bio, phoneNumber)
   * - Organization context
   * 
   * @example
   * ```typescript
   * this.userMgmtService.getOrganizationUser('orgId', 'userId')
   *   .subscribe({
   *     next: response => {
   *       const user = response.data.user;
   *       console.log(`${user.name} - ${user.organizationRole}`);
   *       console.log('Email:', user.email);
   *       console.log('Last login:', user.lastLoginAt);
   *     },
   *     error: err => console.error('Failed:', err)
   *   });
   * ```
   */
  getOrganizationUser(orgId: string, userId: string): Observable<GetOrganizationUserResponse> {
    this.logDebug('Getting organization user', { orgId, userId });

    return this.http
      .get<GetOrganizationUserResponse>(`${this.apiUrl}/organizations/${orgId}/users/${userId}`)
      .pipe(
        map(response => {
          this.logDebug('Organization user retrieved', { userId });
          return response;
        }),
        catchError(error => this.handleError('getOrganizationUser', error))
      );
  }

  // ============================================================================
  // UPDATE OPERATIONS
  // ============================================================================

  /**
   * Change user's role in organization
   * 
   * @param orgId - Organization ID
   * @param userId - User ID
   * @param dto - Role change data
   * @returns Observable of API response with updated user
   * 
   * @throws {Error} 400 - Invalid data
   * @throws {Error} 401 - Unauthorized
   * @throws {Error} 403 - Forbidden (Admin role required)
   * @throws {Error} 404 - Organization or user not found
   * 
   * @remarks
   * **Admin Only** - Requires Admin role in the organization.
   * 
   * **Valid Roles:**
   * - Admin: Full control over organization
   * - Editor: Can create and edit content
   * - Reader: Read-only access
   * 
   * @example
   * ```typescript
   * // Promote user to Admin
   * this.userMgmtService.changeUserRole(
   *   '507f1f77bcf86cd799439011',
   *   '507f1f77bcf86cd799439012',
   *   { role: 'Admin' }
   * ).subscribe({
   *   next: response => {
   *     console.log('Role updated:', response.message);
   *     console.log('New role:', response.data.user.organizationRole);
   *   },
   *   error: err => console.error('Failed to change role:', err)
   * });
   * 
   * // Demote user to Reader
   * this.userMgmtService.changeUserRole('orgId', 'userId', { role: 'Reader' })
   *   .subscribe(response => {
   *     console.log('User is now Reader');
   *   });
   * ```
   */
  changeUserRole(
    orgId: string,
    userId: string,
    dto: ChangeUserRoleDto
  ): Observable<ChangeUserRoleResponse> {
    this.logDebug('Changing user role', { orgId, userId, role: dto.role });

    return this.http
      .patch<ChangeUserRoleResponse>(
        `${this.apiUrl}/organizations/${orgId}/users/${userId}/role`,
        dto
      )
      .pipe(
        map(response => {
          this.logDebug('User role changed successfully', { userId, role: dto.role });
          return response;
        }),
        catchError(error => this.handleError('changeUserRole', error))
      );
  }

  // ============================================================================
  // DELETE OPERATIONS
  // ============================================================================

  /**
   * Remove user from organization
   * 
   * @param orgId - Organization ID
   * @param userId - User ID to remove
   * @param dto - Optional removal data (e.g., reason)
   * @returns Observable of API response confirming removal
   * 
   * @throws {Error} 400 - Invalid data or cannot remove creator
   * @throws {Error} 401 - Unauthorized
   * @throws {Error} 403 - Forbidden (Admin role required)
   * @throws {Error} 404 - Organization or user not found
   * 
   * @remarks
   * **Admin Only** - Requires Admin role in the organization.
   * 
   * **Restrictions:**
   * - Cannot remove the organization creator
   * - User is removed from organization members
   * - User's data in other organizations is not affected
   * 
   * @example
   * ```typescript
   * this.userMgmtService.removeUserFromOrganization(
   *   '507f1f77bcf86cd799439011',
   *   '507f1f77bcf86cd799439012'
   * ).subscribe({
   *   next: response => {
   *     console.log(response.message);
   *     // Update UI, remove user from list
   *   },
   *   error: err => {
   *     if (err.status === 400) {
   *       console.error('Cannot remove organization creator');
   *     }
   *   }
   * });
   * 
   * // With reason
   * this.userMgmtService.removeUserFromOrganization('orgId', 'userId', {
   *   reason: 'User requested account removal'
   * }).subscribe(response => {
   *   console.log('User removed');
   * });
   * ```
   */
  removeUserFromOrganization(
    orgId: string,
    userId: string,
    dto?: RemoveUserDto
  ): Observable<RemoveUserResponse> {
    this.logDebug('Removing user from organization', { orgId, userId });

    const options = dto ? { body: dto } : {};

    return this.http
      .delete<RemoveUserResponse>(
        `${this.apiUrl}/organizations/${orgId}/users/${userId}`,
        options
      )
      .pipe(
        map(response => {
          this.logDebug('User removed from organization', { userId });
          return response;
        }),
        catchError(error => this.handleError('removeUserFromOrganization', error))
      );
  }

  /**
   * Fully offboard a member from an organization (content transfer included)
   *
   * @param orgId  - MongoDB `_id` of the organization
   * @param userId - MongoDB `_id` of the member to remove (**not** the Firebase UID)
   * @returns Observable of API response with removal and content-transfer summary
   *
   * @throws {Error} 400 - Invalid `orgId` or `userId` format
   * @throws {Error} 401 - Missing or invalid Firebase token
   * @throws {Error} 403 - Caller is not an Admin, target is an Admin, or caller tried to remove themselves
   * @throws {Error} 404 - Organization or target user not found / not a member
   *
   * @remarks
   * **Admin Only** — Requires Admin role in the organization.
   *
   * Unlike `removeUserFromOrganization` (which performs a basic membership deletion),
   * this method calls the full offboarding endpoint which also:
   * - Transfers blog post ownership to the requesting admin (Editors only)
   * - Transfers media and file ownership to the requesting admin (all roles)
   * - Removes the user from all groups within the organization
   * - Unlinks the organization from the user record without deleting the account
   *
   * Non-fatal transfer failures are surfaced in `data.warnings`; the membership
   * removal is still considered successful in that case.
   *
   * @example
   * ```typescript
   * this.userMgmtService.removeMember('orgId', 'userId').subscribe({
   *   next: res => {
   *     const { contentTransfer, warnings } = res.data;
   *     if (warnings.length > 0) {
   *       // Surface dismissible warning banner in the UI
   *       console.warn('Member removed with warnings:', warnings);
   *     }
   *     console.info('Content transfer summary:', contentTransfer);
   *   },
   *   error: err => {
   *     // err.status === 403 → target is Admin or caller tried to remove themselves
   *     console.error('Offboarding failed:', err.message);
   *   }
   * });
   * ```
   */
  removeMember(orgId: string, userId: string): Observable<RemoveMemberResponse> {
    this.logDebug('Offboarding member from organization', { orgId, userId });

    return this.http
      .delete<RemoveMemberResponse>(
        `${this.apiUrl}/organizations/${orgId}/members/${userId}`
      )
      .pipe(
        map(response => {
          this.logDebug('Member offboarded successfully', {
            userId,
            transferred: response.data.contentTransfer
          });
          return response;
        }),
        catchError(error => this.handleError('removeMember', error))
      );
  }

  // ============================================================================
  // CONVENIENCE METHODS
  // ============================================================================

  /**
   * Get organization users data without API wrapper
   * 
   * @param orgId - Organization ID
   * @param params - Query parameters
   * @returns Observable of users array
   */
  getOrganizationUsersData(
    orgId: string,
    params?: OrganizationUserQueryParams
  ): Observable<OrganizationUser[]> {
    return this.getOrganizationUsers(orgId, params).pipe(
      map(response => response.data.users)
    );
  }

  /**
   * Get organization user data without API wrapper
   * 
   * @param orgId - Organization ID
   * @param userId - User ID
   * @returns Observable of user
   */
  getOrganizationUserData(orgId: string, userId: string): Observable<OrganizationUserDetailed> {
    return this.getOrganizationUser(orgId, userId).pipe(
      map(response => response.data.user)
    );
  }

  /**
   * Get count of users in organization
   * 
   * @param orgId - Organization ID
   * @param params - Optional query parameters
   * @returns Observable of user count
   */
  getOrganizationUsersCount(orgId: string, params?: OrganizationUserQueryParams): Observable<number> {
    return this.getOrganizationUsers(orgId, params).pipe(
      map(response => response.data.pagination.total)
    );
  }

  /**
   * Search users in organization
   * 
   * @param orgId - Organization ID
   * @param searchTerm - Search query
   * @param page - Page number
   * @param limit - Items per page
   * @returns Observable of API response
   */
  searchOrganizationUsers(
    orgId: string,
    searchTerm: string,
    page: number = 1,
    limit: number = 10
  ): Observable<GetOrganizationUsersResponse> {
    return this.getOrganizationUsers(orgId, { search: searchTerm, page, limit });
  }

  /**
   * Get users by role in organization
   * 
   * @param orgId - Organization ID
   * @param role - Role to filter by
   * @returns Observable of users array
   */
  getUsersByRole(orgId: string, role: 'Admin' | 'Editor' | 'Reader'): Observable<OrganizationUser[]> {
    return this.getOrganizationUsers(orgId, { role }).pipe(
      map(response => response.data.users)
    );
  }

  // ============================================================================
  // PRIVATE HELPER METHODS
  // ============================================================================

  /**
   * Handle HTTP errors with proper error transformation
   * 
   * @param operation - Name of the operation that failed
   * @param error - HTTP error response
   * @returns Observable that throws formatted error
   */
  private handleError(operation: string, error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An unknown error occurred';

    if (error.error instanceof ErrorEvent) {
      // Client-side or network error
      errorMessage = `Client error: ${error.error.message}`;
    } else {
      // Backend error
      if (error.error?.message) {
        errorMessage = error.error.message;
      } else if (error.status) {
        errorMessage = `Server error: ${error.status} - ${error.statusText}`;
      }
    }

    this.logDebug(`${operation} failed`, { error: errorMessage, status: error.status });

    return throwError(() => ({
      operation,
      message: errorMessage,
      status: error.status,
      originalError: error
    }));
  }

  /**
   * Log debug information if debug mode is enabled
   * 
   * @param message - Debug message
   * @param data - Optional data to log
   */
  private logDebug(message: string, data?: any): void {
    if (this.config.debug) {
      console.log(`[UserManagementService] ${message}`, data || '');
    }
  }
}
